import React, { useState, useMemo } from "react";
import {
  Droplets,
  Wind,
  AudioLines,
  Factory,
  Fish,
  ShieldCheck,
  Gauge,
  Headphones,
  TrendingUp,
  Mail,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const heroImage = "/Images/services/enviromental/equipment/equipos.webp";

const normalize = (text = "") =>
  text
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// CATEGORÍAS
const categories = [
  { label: "Todos", icon: null },
  { label: "Calidad de aguas", icon: Droplets },
  { label: "Calidad de aire", icon: Wind },
  { label: "Monitoreo de ruido", icon: AudioLines },
  { label: "Fuentes fijas", icon: Factory },
  { label: "Hidrobiología", icon: Fish },
];

// DATA EQUIPOS
const data = [
  // CALIDAD DE AIRE
  {
    category: "Calidad de aire",
    image: "/Images/services/enviromental/equipment/combustion.webp",
    title: "Analizador de combustión PCA 400",
    brand: "Bacharach PCA® 400",
    description:
      "Analizador portátil para la medición de gases de combustión y la optimización de sistemas térmicos, utilizado en la puesta en marcha, ajuste, mantenimiento y control del desempeño de calderas, hornos y procesos industriales.",
    features: [
      "Medición de CO, CO₂, O₂",
      "Configuración opcional de sensores para NO, NO₂ y SO₂",
      "Cálculo de eficiencia de combustión",
      "Registro y almacenamiento de datos",
      "Pantalla digital de fácil lectura",
      "Equipo portátil para campo e industria",
    ],
    applications: [
      "Auditorías energéticas",
      "Mantenimiento de calderas",
      "Control y monitoreo de emisiones",
      "Optimización de procesos térmicos industriales",
    ],
  },
  {
    category: "Calidad de aire",
    image: "/Images/services/enviromental/equipment/amb.webp",
    title: "Analizador de dióxido de azufre (SO₂)",
    brand: "Horiba APSA-370",
    description:
      "Analizador automático utilizado para la medición continua de dióxido de azufre (SO₂) en aire ambiente. Utiliza tecnología de fluorescencia ultravioleta (UV) para proporcionar mediciones precisas y confiables en aplicaciones de monitoreo de calidad del aire y control de la contaminación atmosférica.",
    features: [
      "Medición continua de SO₂ en aire ambiente",
      "Tecnología de fluorescencia UV de alta sensibilidad",
      "Monitoreo en tiempo real",
      "Registro y almacenamiento continuo de datos",
      "Sistema de muestreo de método seco de bajo mantenimiento",
      "Alta precisión y estabilidad operativa",
    ],
    applications: [
      "Redes de vigilancia de calidad del aire",
      "Monitoreo de contaminación atmosférica",
      "Estudios e investigaciones ambientales",
      "Evaluación de impacto ambiental",
      "Seguimiento de cumplimiento normativo",
    ],
  },
  {
    category: "Calidad de aire",
    image: "/Images/services/enviromental/equipment/amb1.webp",
    title: "Analizador de monóxido de carbono (CO)",
    brand: "Horiba APMA-370",
    description:
      "Analizador automático utilizado para la medición continua de monóxido de carbono (CO) en aire ambiente. Utiliza tecnología infrarroja no dispersiva (NDIR) de modulación cruzada para proporcionar mediciones precisas y confiables en aplicaciones de monitoreo de calidad del aire y control de emisiones.",
    features: [
      "Medición continua de CO en aire ambiente",
      "Tecnología infrarroja no dispersiva (NDIR) de alta sensibilidad",
      "Monitoreo en tiempo real",
      "Registro y almacenamiento continuo de datos",
      "Sistema de muestreo por método seco de bajo mantenimiento",
      "Alta precisión y estabilidad operativa",
    ],
    applications: [
      "Redes de vigilancia de calidad del aire",
      "Monitoreo de contaminación atmosférica",
      "Control y seguimiento de emisiones",
      "Estudios e investigaciones ambientales",
      "Cumplimiento normativo y monitoreo ambiental",
    ],
  },
  {
    category: "Calidad de aire",
    image: "/Images/services/enviromental/equipment/AMN.webp",
    title: "Analizador de óxidos de nitrógeno (NOx)",
    brand: "Horiba APNA-370",
    description:
      "Analizador automático utilizado para la medición continua de óxidos de nitrógeno (NO, NO₂ y NOx) en aire ambiente. Utiliza tecnología de quimioluminiscencia de alta sensibilidad para proporcionar mediciones precisas y confiables en aplicaciones de monitoreo de calidad del aire y control de la contaminación atmosférica.",
    features: [
      "Medición continua de NO, NO₂ y NOx",
      "Tecnología de quimioluminiscencia de alta sensibilidad",
      "Monitoreo en tiempo real",
      "Registro y almacenamiento continuo de datos",
      "Sistema de muestreo por método seco de bajo mantenimiento",
      "Alta precisión y estabilidad operativa",
    ],
    applications: [
      "Redes de vigilancia de calidad del aire",
      "Monitoreo de contaminación atmosférica",
      "Control y seguimiento de emisiones",
      "Estudios e investigaciones ambientales",
      "Cumplimiento normativo y monitoreo ambiental",
    ],
  },
  {
    category: "Calidad de aire",
    image: "/Images/services/enviromental/equipment/AMN.webp",
    title: "Analizador de ozono ambiental (O₃)",
    brand: "Horiba APOA-370",
    description:
      "Analizador automático utilizado para la medición continua de ozono (O₃) en aire ambiente. Utiliza tecnología de absorción ultravioleta (UV) de alta sensibilidad para proporcionar mediciones precisas y confiables en aplicaciones de monitoreo de calidad del aire y control de la contaminación atmosférica.",
    features: [
      "Medición continua de ozono (O₃) en aire ambiente",
      "Tecnología de absorción ultravioleta (UV) de alta sensibilidad",
      "Monitoreo en tiempo real",
      "Registro y almacenamiento continuo de datos",
      "Sistema de muestreo por método seco de bajo mantenimiento",
      "Alta precisión y estabilidad operativa",
    ],
    applications: [
      "Redes de vigilancia de calidad del aire",
      "Monitoreo de ozono troposférico",
      "Control de contaminación atmosférica",
      "Estudios e investigaciones ambientales",
      "Cumplimiento normativo y monitoreo ambiental",
    ],
  },

  // CALIDAD DE AGUAS
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/flujumetro.webp",
    title: "Flujómetro portátil de corriente de agua",
    brand: "Modelo 94303",
    description:
      "Flujómetro portátil para medición de caudal y velocidad de flujo en cuerpos de agua. Diseñado para estudios hidrológicos en ríos, canales y sistemas de alcantarillado, permitiendo mediciones precisas en campo.",
    features: [
      "Medición de velocidad instantánea y promedio del flujo",
      'Sensor tipo Turbo-Prop de 2"',
      "Pantalla digital de fácil lectura",
      "Memoria para hasta 30 registros",
      "Varilla extensible de 3 a 6 pies, ampliable hasta 25 pies",
      "Equipo portátil para uso en campo",
    ],
    applications: [
      "Estudios hidrológicos de campo",
      "Monitoreo de ríos, canales y alcantarillado",
      "Evaluaciones hidráulicas",
      "Medición de caudal en cuerpos de agua",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/multiparametro.webp",
    title: "Colorímetro portátil",
    brand: "Hach DR/890 Colorimeter",
    description:
      "Colorímetro portátil para análisis de calidad de agua en campo mediante métodos colorimétricos. Diseñado para la determinación de múltiples parámetros fisicoquímicos utilizando reactivos específicos, con alta precisión y facilidad de uso en sitio.",
    features: [
      "Equipo portátil para análisis en campo",
      "Amplia biblioteca de métodos colorimétricos preprogramados",
      "Pantalla digital de fácil lectura",
      "Teclado integrado para operación sencilla",
      "Carcasa resistente para uso en campo",
      "Resultados rápidos mediante análisis con reactivos",
    ],
    applications: [
      "Análisis de calidad de agua en campo",
      "Monitoreo de parámetros fisicoquímicos",
      "Control de calidad de agua potable y residual",
      "Estudios ambientales",
      "Muestreo y análisis en sitio",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/multi.webp",
    title: "Multiparámetro portátil avanzado",
    brand: "WTW MultiLine Multi 3620 IDS portátil",
    description:
      "Medidor multiparámetro portátil de alta precisión con dos canales universales, diseñado para análisis simultáneo de parámetros en agua mediante sensores digitales IDS. Permite mediciones en campo y laboratorio con alta estabilidad y trazabilidad de datos.",

    features: [
      "Dos canales universales para medición simultánea",
      "Medición de pH, ORP, oxígeno disuelto y turbidez",
      "Compatibilidad con sensores digitales IDS",
      "Conexión por cable o inalámbrica",
      "Pantalla gráfica a color con visualización de múltiples parámetros",
      "Funciones CMC y QSC",
      "Memoria para 10.000 registros automáticos y 500 manuales",
      "Transferencia de datos vía USB",
      "Diseño robusto con protección IP67",
    ],

    applications: [
      "Monitoreo ambiental",
      "Control de calidad de agua",
      "Investigación ambiental",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/zooplancton.webp",
    title: "Red de zooplancton",
    brand: "Red Zooplancton",
    description:
      "Red de muestreo diseñada en forma cónica para la recolección de zooplancton en cuerpos de agua, permitiendo el paso del agua mientras filtra organismos pequeños para su análisis.",

    features: [
      "Diseño cónico para muestreo de zooplancton",
      "Malla de nylon de 55 micras",
      "Marco de 30 cm de diámetro",
      "Bolsa de recolección de 500 mm",
      "Depósito colector con malla de nylon",
      "Filtración eficiente de pequeñas muestras biológicas",
      "Permite circulación de agua durante el muestreo",
    ],

    applications: [
      "Estudios biológicos acuáticos",
      "Hidrobiología",
      "Muestreo de zooplancton en cuerpos de agua",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/fitoplancton.webp",
    title: "Red de fitoplancton",
    brand: "Red Fitoplancton",
    description:
      "Red de muestreo cónica para la recolección de fitoplancton en ambientes acuáticos, diseñada para la filtración de organismos microscópicos mediante malla de alta precisión.",

    features: [
      "Diseño cónico para muestreo de fitoplancton",
      "Longitud de 1,20 m",
      "Diámetro de boca de 30 cm",
      "Malla de 25 micras",
      "Permite la recolección de organismos microscópicos en cuerpos de agua",
    ],

    applications: [
      "Estudios ecológicos acuáticos",
      "Monitoreo de calidad de agua",
      "Análisis de fitoplancton en cuerpos de agua",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/surber.webp",
    title: "Red Surber",
    brand: "Red Surber",
    description:
      "Equipo de muestreo tipo Surber para la recolección de macroinvertebrados bentónicos en sustratos de ríos, utilizado en estudios de bioindicación y calidad de agua.",

    features: [
      "Marco cuadrado de 30 × 30 cm (área de muestreo de 0.09 m²)",
      "Dos marcos plegables de aluminio para fácil transporte y protección de la malla",
      "Malla disponible en 150, 300 o 500 µm según el objetivo del muestreo",
      "Manga cónica con profundidad de 60 a 75 cm",
      "Diseño para muestreo directo en sustratos de río",
      "Recolección eficiente de macroinvertebrados bentónicos",
    ],

    applications: [
      "Bioindicadores de calidad de agua",
      "Estudios de biodiversidad acuática",
      "Monitoreo de ecosistemas fluviales",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/red-ictioplancton.webp",
    title: "Red de ictioplancton",
    brand: "Red Ictioplancton",
    description:
      "Red especializada para muestreo de ictioplancton en ambientes acuáticos, diseñada para la captura de organismos en etapas tempranas de desarrollo en la columna de agua.",

    features: [
      "Tela filtrante con poro efectivo de 300 micrómetros",
      "Material de poliéster color blanco",
      "Diseñada para muestreo de ictioplancton en columna de agua",
      "Filtro verificado microscópicamente según datos del fabricante",
      "Material compatible con análisis microscópico",
    ],

    applications: [
      "Estudios de reproducción de peces",
      "Ecología acuática",
      "Monitoreo de larvas de peces en cuerpos de agua",
    ],
  },
  {
    category: "Calidad de aguas",
    image: "/Images/services/enviromental/equipment/nasa.webp",
    title: "Nasa de mano",
    brand: "Nasa de mano",
    description:
      "Red manual para captura de organismos acuáticos en campo, diseñada para muestreos rápidos desde orilla o en zonas de vadeo.",

    features: [
      "Aro de 42 cm de diámetro",
      "Mango de aluminio de 1.2 m",
      "Malla de nylon con apresto",
      "Abertura de malla de 4 mm",
      "Estructura liviana para uso prolongado en campo",
      "Diseñada para capturas rápidas de organismos acuáticos",
    ],

    applications: [
      "Muestreo biológico rápido",
      "Trabajo de campo en ambientes acuáticos",
    ],
  },

  // HIDROBIOLOGÍA
  {
    category: "Hidrobiología",
    image: "/Images/services/enviromental/equipment/zooplancton.webp",
    title: "Red de zooplancton",
    brand: "Red Zooplancton",
    description:
      "Red de muestreo diseñada en forma cónica para la recolección de zooplancton en cuerpos de agua, permitiendo el paso del agua mientras filtra organismos pequeños para su análisis.",

    features: [
      "Diseño cónico para muestreo de zooplancton",
      "Malla de nylon de 55 micras",
      "Marco de 30 cm de diámetro",
      "Bolsa de recolección de 500 mm",
      "Depósito colector con malla de nylon",
      "Filtración eficiente de pequeñas muestras biológicas",
      "Permite circulación de agua durante el muestreo",
    ],

    applications: [
      "Estudios biológicos acuáticos",
      "Hidrobiología",
      "Muestreo de zooplancton en cuerpos de agua",
    ],
  },
  {
    category: "Hidrobiología",
    image: "/Images/services/enviromental/equipment/fitoplancton.webp",
    title: "Red de fitoplancton",
    brand: "Red Fitoplancton",
    description:
      "Red de muestreo cónica para la recolección de fitoplancton en ambientes acuáticos, diseñada para la filtración de organismos microscópicos mediante malla de alta precisión.",

    features: [
      "Diseño cónico para muestreo de fitoplancton",
      "Longitud de 1,20 m",
      "Diámetro de boca de 30 cm",
      "Malla de 25 micras",
      "Permite la recolección de organismos microscópicos en cuerpos de agua",
    ],

    applications: [
      "Estudios ecológicos acuáticos",
      "Monitoreo de calidad de agua",
      "Análisis de fitoplancton en cuerpos de agua",
    ],
  },
  {
    category: "Hidrobiología",
    image: "/Images/services/enviromental/equipment/surber.webp",
    title: "Red Surber",
    brand: "Red Surber",
    description:
      "Equipo de muestreo tipo Surber para la recolección de macroinvertebrados bentónicos en sustratos de ríos, utilizado en estudios de bioindicación y calidad de agua.",

    features: [
      "Marco cuadrado de 30 × 30 cm (área de muestreo de 0.09 m²)",
      "Dos marcos plegables de aluminio para fácil transporte y protección de la malla",
      "Malla disponible en 150, 300 o 500 µm según el objetivo del muestreo",
      "Manga cónica con profundidad de 60 a 75 cm",
      "Diseño para muestreo directo en sustratos de río",
      "Recolección eficiente de macroinvertebrados bentónicos",
    ],

    applications: [
      "Bioindicadores de calidad de agua",
      "Estudios de biodiversidad acuática",
      "Monitoreo de ecosistemas fluviales",
    ],
  },
  {
    category: "Hidrobiología",
    image: "/Images/services/enviromental/equipment/red-ictioplancton.webp",
    title: "Red de ictioplancton",
    brand: "Red Ictioplancton",
    description:
      "Red especializada para muestreo de ictioplancton en ambientes acuáticos, diseñada para la captura de organismos en etapas tempranas de desarrollo en la columna de agua.",

    features: [
      "Tela filtrante con poro efectivo de 300 micrómetros",
      "Material de poliéster color blanco",
      "Diseñada para muestreo de ictioplancton en columna de agua",
      "Filtro verificado microscópicamente según datos del fabricante",
      "Material compatible con análisis microscópico",
    ],

    applications: [
      "Estudios de reproducción de peces",
      "Ecología acuática",
      "Monitoreo de larvas de peces en cuerpos de agua",
    ],
  },
  {
    category: "Hidrobiología",
    image: "/Images/services/enviromental/equipment/nasa.webp",
    title: "Nasa de mano",
    brand: "Nasa de mano",
    description:
      "Red manual para captura de organismos acuáticos en campo, diseñada para muestreos rápidos desde orilla o en zonas de vadeo.",

    features: [
      "Aro de 42 cm de diámetro",
      "Mango de aluminio de 1.2 m",
      "Malla de nylon con apresto",
      "Abertura de malla de 4 mm",
      "Estructura liviana para uso prolongado en campo",
      "Diseñada para capturas rápidas de organismos acuáticos",
    ],

    applications: [
      "Muestreo biológico rápido",
      "Trabajo de campo en ambientes acuáticos",
    ],
  },
];

//POR QUÉ ELEGIRNOS
const rentalAdvantages = [
  {
    title: "Equipos calibrados y listos para uso",
    desc: "Garantizamos mediciones exactas y confiables desde el primer uso.",
    icon: ShieldCheck,
  },
  {
    title: "Tecnología de alta precisión",
    desc: "Instrumentos modernos para resultados técnicos confiables.",
    icon: Gauge,
  },
  {
    title: "Soporte técnico especializado",
    desc: "Acompañamiento antes, durante y después del alquiler.",
    icon: Headphones,
  },
  {
    title: "Optimización de costos operativos",
    desc: "Evita inversión en compra de equipos especializados.",
    icon: TrendingUp,
  },
];

const ITEMS_PER_PAGE = 16;

const shuffleArray = (array) => {
  const arr = [...array];

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
};

const Equipament = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };
  const [category, setCategory] = useState("Todos");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const base =
      category === "Todos"
        ? data
        : data.filter(
            (item) => normalize(item.category) === normalize(category),
          );

    const seen = new Set();

    const unique = base.filter((item) => {
      const key = item.title.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return shuffleArray(unique);
  }, [category]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  }, [filtered]);

  const paginated = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, page]);

  const handleCategory = (cat) => {
    setCategory(cat);
    setPage(1);
  };

  return (
    <>
      {/* HERO */}
      <section className="overflow-hidden">
        <div className="relative h-[420px] sm:h-[500px] md:h-[620px] overflow-hidden">
          <img
            src={heroImage}
            alt="Alquiler de equipos ambientales"
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-transparent" />

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ee7d32]/15 border border-[#ee7d32]/30 text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] backdrop-blur-sm">
                  Servicios
                </span>

                <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[0.95]">
                  Alquiler de <br />
                  <span className="text-[#ee7d32]">Equipos</span>
                </h1>

                <p className="mt-6 text-white/80 text-sm sm:text-base md:text-lg leading-7 max-w-xl">
                  Equipos ambientales de alta precisión para mediciones
                  confiables.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATÁLOGO */}
      <section className="py-24 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-14 max-w-2xl">
            <span className="text-[#ee7d32] uppercase text-sm tracking-[0.25em]">
              Equipos disponibles
            </span>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold">
              Catálogo de equipos
            </h2>
          </div>

          {/* CATEGORÍAS */}
          <div className="mb-10 flex justify-center">
            <div className="w-full max-w-5xl bg-white border rounded-2xl p-3 shadow-sm">
              <div className="md:hidden">
                <select
                  value={category}
                  onChange={(e) => handleCategory(e.target.value)}
                  className="w-full p-3 rounded-xl border text-sm"
                >
                  {categories.map((cat, i) => (
                    <option key={i} value={cat.label}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="hidden md:flex flex-wrap justify-center gap-2">
                {categories.map((cat, i) => {
                  const Icon = cat.icon;
                  const active = category === cat.label;

                  return (
                    <button
                      key={i}
                      onClick={() => handleCategory(cat.label)}
                      className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition ${
                        active
                          ? "bg-[#ee7d32] text-white"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {Icon && <Icon size={16} />}
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            {paginated.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center py-16 text-center max-w-lg mx-auto">
                <h3 className="text-xl font-semibold text-gray-800">
                  No hay equipos disponibles actualmente en esta categoría.
                </h3>

                <p className="text-gray-500 mt-3 text-sm leading-relaxed">
                  Puedes consultar otras soluciones técnicas dentro de nuestro
                  portafolio o solicitar asesoría especializada para tu
                  operación.
                </p>

                <button
                  onClick={() => handleCategory("Todos")}
                  className="mt-6 px-6 py-2.5 bg-[#ee7d32] text-white rounded-xl text-sm font-semibold hover:bg-[#e56f20] transition"
                >
                  Ver catálogo completo
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                {paginated.map((item, i) => (
                  <div
                    key={i}
                    className="group bg-white rounded-2xl border shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full overflow-hidden"
                  >
                    <div className="h-[220px] bg-gray-50 flex items-center justify-center p-5">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-bold text-[#111] text-base leading-tight line-clamp-2 min-h-[48px]">
                        {item.title}
                      </h3>

                      <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                        {item.brand}
                      </p>

                      <div className="mt-auto flex flex-col gap-2 pt-5">
                        <Link
                          to={`/contact-us?division=ambiental&service=Alquiler de equipos&equipment=${encodeURIComponent(item.title)}`}
                          className="w-full py-2.5 rounded-xl bg-[#ee7d32] text-white text-sm font-semibold hover:bg-[#e56f20] transition flex items-center justify-center"
                        >
                          Alquilar
                        </Link>

                        <button
                          onClick={() => handleOpenModal(item)}
                          className="w-full py-2.5 rounded-xl border border-[#ee7d32] text-[#ee7d32] text-sm font-semibold hover:bg-[#ee7d32] hover:text-white transition"
                        >
                          Ver detalles
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center mt-10 gap-2 flex-wrap">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`w-10 h-10 rounded-full text-sm font-semibold transition ${
                    page === i + 1
                      ? "bg-[#ee7d32] text-white"
                      : "bg-white border hover:bg-gray-100"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* VENTANA MODAL DETALLES EQUIPOS */}
      {isModalOpen && selectedItem && (
        <div
          className="
      fixed
      inset-0
      z-50
      flex
      items-center
      justify-center
      bg-black/60
      backdrop-blur-md
      p-4
      sm:p-6
    "
        >
          <div
            className="
        relative
        flex
        flex-col
        bg-white
        w-full
        max-w-4xl
        max-h-[calc(100vh-2rem)]
        sm:max-h-[calc(100vh-3rem)]
        rounded-3xl
        shadow-2xl
        overflow-hidden
        animate-fadeIn
      "
          >
            <div
              className="
          flex
          items-center
          justify-between
          gap-4
          px-5
          sm:px-6
          py-4
          sm:py-5
          border-b
          bg-gradient-to-r
          from-white
          to-[#fafafa]
          shrink-0
        "
            >
              <div className="min-w-0">
                <h2
                  className="
              text-xl
              sm:text-2xl
              font-bold
              text-gray-900
              leading-tight
              truncate
            "
                >
                  {selectedItem.title}
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  Ficha técnica del equipo
                </p>
              </div>

              <button
                type="button"
                onClick={handleCloseModal}
                aria-label="Cerrar modal"
                className="
            shrink-0
            w-10
            h-10
            flex
            items-center
            justify-center
            rounded-full
            text-gray-500
            hover:bg-[#ee7d32]/10
            hover:text-[#ee7d32]
            transition-all
            duration-300
          "
              >
                <span className="text-xl leading-none">✕</span>
              </button>
            </div>

            <div
              className="
          flex-1
          overflow-y-auto
          overscroll-contain
          scrollbar-thin
          scrollbar-thumb-gray-300
          scrollbar-track-transparent
        "
            >
              <div
                className="
            grid
            grid-cols-1
            md:grid-cols-2
            gap-6
            lg:gap-8
            p-5
            sm:p-6
            lg:p-8
          "
              >
                <div
                  className="
              flex
              items-center
              justify-center
              bg-gradient-to-br
              from-gray-50
              to-white
              rounded-2xl
              p-6
              sm:p-8
              border
              border-gray-100
              min-h-[220px]
              md:min-h-[320px]
              md:sticky
              md:top-0
            "
                >
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.title}
                    className="
                max-h-[220px]
                sm:max-h-[280px]
                md:max-h-[320px]
                w-full
                object-contain
                transition-transform
                duration-300
                hover:scale-105
              "
                  />
                </div>

                <div className="space-y-5 sm:space-y-6">
                  <div>
                    <span
                      className="
                  inline-flex
                  items-center
                  px-3
                  py-1
                  rounded-full
                  bg-[#ee7d32]/10
                  text-[#ee7d32]
                  text-xs
                  font-semibold
                "
                    >
                      {selectedItem.brand}
                    </span>
                  </div>

                  <div>
                    <p
                      className="
                  text-sm
                  text-gray-600
                  leading-7
                "
                    >
                      {selectedItem.description}
                    </p>
                  </div>

                  {selectedItem.features?.length > 0 && (
                    <div
                      className="
                  bg-gray-50
                  rounded-2xl
                  p-4
                  sm:p-5
                  border
                  border-gray-100
                "
                    >
                      <h3
                        className="
                    font-semibold
                    text-gray-900
                    mb-3
                    text-sm
                  "
                      >
                        Características
                      </h3>

                      <ul className="space-y-2">
                        {selectedItem.features.map((f, i) => (
                          <li
                            key={i}
                            className="
                        flex
                        items-start
                        gap-2
                        text-sm
                        text-gray-600
                        leading-6
                      "
                          >
                            <span
                              className="
                          text-[#ee7d32]
                          font-bold
                          mt-0.5
                          shrink-0
                        "
                            >
                              •
                            </span>

                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedItem.applications?.length > 0 && (
                    <div
                      className="
                  bg-gray-50
                  rounded-2xl
                  p-4
                  sm:p-5
                  border
                  border-gray-100
                "
                    >
                      <h3
                        className="
                    font-semibold
                    text-gray-900
                    mb-3
                    text-sm
                  "
                      >
                        Aplicaciones
                      </h3>

                      <ul className="space-y-2">
                        {selectedItem.applications.map((a, i) => (
                          <li
                            key={i}
                            className="
                        flex
                        items-start
                        gap-2
                        text-sm
                        text-gray-600
                        leading-6
                      "
                          >
                            <span
                              className="
                          text-[#ee7d32]
                          font-bold
                          mt-0.5
                          shrink-0
                        "
                            >
                              •
                            </span>

                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className="
          shrink-0
          px-5
          sm:px-6
          py-4
          sm:py-5
          border-t
          border-gray-100
          bg-white
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-3
        "
            >
              <button
                type="button"
                onClick={handleCloseModal}
                className="
            w-full
            sm:w-auto
            px-5
            py-2.5
            rounded-xl
            border
            border-gray-200
            text-gray-600
            font-medium
            hover:bg-gray-50
            hover:border-gray-300
            transition-all
            duration-300
          "
              >
                Cerrar
              </button>

              <Link
                to={`/contact-us?division=ambiental&service=Alquiler de equipos&equipment=${encodeURIComponent(
                  selectedItem.title,
                )}`}
                className="
            w-full
            sm:w-auto
            px-6
            py-2.5
            rounded-xl
            bg-[#ee7d32]
            text-white
            font-semibold
            hover:bg-[#e56f20]
            hover:shadow-lg
            hover:shadow-[#ee7d32]/20
            transition-all
            duration-300
            text-center
          "
              >
                Solicitar alquiler del equipo
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* POR QUÉ ELEGIRNOS */}
      <section className="relative py-20 md:py-20 bg-[#f8f7f4] overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#ee7d32]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-[#ee7d32]/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1]">
              ¿Por qué alquilar{" "}
              <span className="text-[#ee7d32]">con nosotros?</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-[#555] leading-8 max-w-2xl">
              Soluciones de alquiler de equipos ambientales listos para
              operación en campo, calibrados bajo estándares técnicos y con
              acompañamiento especializado para garantizar mediciones
              confiables.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-16 py-8">
            {rentalAdvantages.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-3xl p-8 pt-14 text-center shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_80px_rgba(238,125,50,0.18)] transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-[#ee7d32] rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <Icon
                      className="w-8 h-8"
                      stroke="#ffff"
                      strokeWidth={2.2}
                    />
                  </div>

                  <h3 className="text-[18px] font-[800] text-[#111]">
                    {item.title}
                  </h3>

                  <p className="text-sm text-gray-600 leading-[1.6] mt-3">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA ALQUILER DE EQUIPOS */}
      <section className="relative py-10 md:py-10 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="group relative overflow-hidden rounded-[30px] border border-[#ececec] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.10)]">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-[#ee7d32] via-[#ee7d32]/50 to-transparent" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 px-6 py-8 md:px-10 md:py-10">
              <div className="flex flex-col sm:flex-row items-start gap-6 max-w-3xl">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-[#ee7d32]/20 blur-xl rounded-2xl" />
                  <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center">
                    <Mail className="w-10 h-10 stroke-[#ee7d32]" />
                  </div>
                </div>

                <div>
                  <span className="inline-flex items-center rounded-full bg-[#ee7d32]/10 border border-[#ee7d32]/20 px-3 py-1 text-[11px] md:text-[12px] font-semibold uppercase tracking-widest text-[#ee7d32]">
                    Alquiler de equipos ambientales
                  </span>

                  <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-[#111] leading-tight">
                    Necesitas equipos para tu proyecto
                  </h3>

                  <p className="mt-4 text-sm md:text-base leading-7 text-[#6b7280] max-w-2xl">
                    Accede al equipo ideal para tus mediciones, listo para
                    operar con precisión desde el primer uso.
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto">
                <Link
                  to="/contact-us?division=ambiental&service=Alquiler de equipos"
                  className="group/btn inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-lg bg-[#ee7d32] px-6 py-3.5 md:px-7 md:py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#e56f20] hover:shadow-lg hover:shadow-[#ee7d32]/20"
                >
                  Solicitar alquiler
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    color="white"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Equipament;
